// ─── src/components/cards/EditorialCard.jsx ──────────────────────────────────
//
// LWD Editorial Card System, one universal card for every LWD property.
//
// Overlay layout (Option 3, agreed design):
//
//   ┌──────────────────────────┐
//   │ LWD                      │  ← top-left, gold, 9px tracked
//   │ THE ATLAS                │  ← collection label, white 70%, 9px tracked
//   │                          │
//   │                          │
//   │           image          │
//   │                          │
//   │                          │
//   │ ITALY                    │  ← category, gold, uppercase
//   │ Tuscany                  │  ← title, Gilda Display
//   └──────────────────────────┘
//
// Hover: image scale 1.03 · gradient deepens · title shifts -2px · gold rule
//
// Tracking: fires impression (500ms debounce) + click + expand events
// into lwd_intelligence_events via the existing track() utility.
// Aura reads from that same table, no separate pathway needed.
//
// Props:
//   collection   "atlas" | "edit" | "publication" | "featured"
//   contentType  "region_guide" | "article" | "magazine_issue"
//   contentId    slug or id, e.g. "tuscany-wedding-guide"
//   title        display name, e.g. "Tuscany"
//   category     shown in gold above title, e.g. "Italy"
//   country      passed to Aura signals, e.g. "Italy"
//   image        hero image URL
//   onNavigate   SPA callback fn(contentId), preferred over href
//   href         fallback URL for non-SPA contexts
//   source       originating page, e.g. "wedding_guides"
//   context      position context, e.g. "italy_grid" | "featured"
//   position     0-based index in its grid (used for stagger delay)
//   large        4:5 aspect ratio instead of 3:4
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useRef } from "react";
import { track } from "../../utils/track";
import { imgUrl } from "../../utils/imgUrl.js";

const GD   = "var(--font-heading-primary)";
const NU   = "var(--font-body)";
const GOLD = "#c4a35a";

const COLLECTION_LABELS = {
  atlas:       "The Atlas",
  edit:        "The LWD Edit",
  publication: "Publication",
  featured:    "Featured",
};

// ── Gradient definitions, rest and hover states ──────────────────────────────
const GRADIENT_REST  = "linear-gradient(to bottom, rgba(0,0,0,0.10) 0%, transparent 28%, rgba(0,0,0,0.52) 62%, rgba(0,0,0,0.82) 100%)";
const GRADIENT_HOVER = "linear-gradient(to bottom, rgba(0,0,0,0.18) 0%, transparent 28%, rgba(0,0,0,0.65) 62%, rgba(0,0,0,0.90) 100%)";

export default function EditorialCard({
  collection   = "atlas",
  contentType  = "region_guide",
  contentId,
  title        = "",
  category     = "",
  country      = "",
  image        = "",
  onNavigate,
  href,
  source       = "",
  context      = "",
  position     = 0,
  large        = false,
}) {
  const ref              = useRef(null);
  const [inView,    setInView]    = useState(false);
  const [hovered,   setHovered]   = useState(false);
  const impressionFired  = useRef(false);
  const impressionTimer  = useRef(null);

  // ── Entrance animation + impression (500ms debounce) ─────────────────────
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (!impressionFired.current) {
            impressionTimer.current = setTimeout(() => {
              impressionFired.current = true;
              fireEvent("editorial_card_impression");
            }, 500);
          }
          obs.disconnect();
        }
      },
      { rootMargin: "0px 0px -80px 0px" }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      clearTimeout(impressionTimer.current);
    };
  }, []);

  // ── Track helper ─────────────────────────────────────────────────────────
  function fireEvent(eventType) {
    track(eventType, {
      // Top-level intelligence fields (hoisted by track())
      country:  country  || undefined,
      region:   contentId || undefined,
      category: contentType || undefined,
      // Metadata (everything else)
      collection,
      context:  context  || undefined,
      source:   source   || undefined,
      position,
      title,
      content_id: contentId || undefined,
    });
  }

  // ── Click handler ─────────────────────────────────────────────────────────
  function handleClick() {
    fireEvent("editorial_card_click");
    if (onNavigate && contentId) {
      onNavigate(contentId);
    } else if (href) {
      window.location.href = href;
    }
  }

  const label = COLLECTION_LABELS[collection] ?? collection.toUpperCase();
  const delay = (position % 3) * 80;

  return (
    <button
      ref={ref}
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label={`${title}${category ? `, ${category}` : ""}, ${label}`}
      style={{
        // Entrance animation
        opacity:   inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(18px)",
        transition: `opacity 0.55s ease ${delay}ms, transform 0.55s ease ${delay}ms`,
        willChange: "transform, opacity",
        // Reset
        background: "none",
        border: "none",
        padding: 0,
        cursor: "pointer",
        textAlign: "left",
        display: "block",
        width: "100%",
      }}
    >
      {/* ── Image container, entire card is the image ───────────────────── */}
      <div style={{
        position: "relative",
        width: "100%",
        aspectRatio: large ? "4/5" : "3/4",
        overflow: "hidden",
        background: "#1a1714",
        display: "block",
      }}>

        {/* Hero image */}
        {image && (
          <img
            src={imgUrl(image, { width: 800, quality: 75 })}
            alt={title}
            loading="lazy"
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
              transition: "transform 0.70s cubic-bezier(0.25,0.46,0.45,0.94)",
              transform: hovered ? "scale(1.03)" : "scale(1)",
            }}
          />
        )}

        {/* Gradient overlay */}
        <div style={{
          position: "absolute",
          inset: 0,
          background: hovered ? GRADIENT_HOVER : GRADIENT_REST,
          transition: "background 0.45s ease",
          pointerEvents: "none",
        }} />

        {/* ── Top-left: LWD + collection label ─────────────────────────── */}
        <div style={{
          position: "absolute",
          top: 14,
          left: 16,
          display: "flex",
          flexDirection: "column",
          gap: 4,
          pointerEvents: "none",
        }}>
          <span style={{
            fontFamily: NU,
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: "0.25em",
            textTransform: "uppercase",
            color: GOLD,
            lineHeight: 1,
          }}>
            LWD
          </span>
          <span style={{
            fontFamily: NU,
            fontSize: 9,
            fontWeight: 500,
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.70)",
            lineHeight: 1,
          }}>
            {label}
          </span>
        </div>

        {/* ── Bottom: category + title + hover rule ────────────────────── */}
        <div style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          padding: large ? "0 20px 22px" : "0 16px 18px",
          pointerEvents: "none",
        }}>

          {/* Category, gold, small caps */}
          {category && (
            <p style={{
              fontFamily: NU,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: GOLD,
              margin: "0 0 7px",
              lineHeight: 1,
            }}>
              {category}
            </p>
          )}

          {/* Title, Gilda, shifts up on hover */}
          <p style={{
            fontFamily: GD,
            fontSize: large ? 26 : 22,
            fontWeight: 400,
            color: "#ffffff",
            margin: 0,
            lineHeight: 1.15,
            transform: hovered ? "translateY(-2px)" : "translateY(0)",
            transition: "transform 0.30s ease",
          }}>
            {title}
          </p>

          {/* Gold rule, appears on hover */}
          <div style={{
            height: 1,
            width: hovered ? 36 : 0,
            background: GOLD,
            marginTop: 10,
            transition: "width 0.30s ease",
          }} />

        </div>
      </div>
    </button>
  );
}
